import { useCallback, useRef } from 'react';

import { useNavigate } from 'react-router-dom';

import { CATEGORY_ORDER, FEATURES_MAP, featuresByCategory } from '@/widgets/pricing/pricingCards/lib/constants';
import { getPlanBeginCheckoutEventData } from '@/widgets/pricing/pricingCards/lib/utils/getPlanBeginCheckoutEventData';

import { useDowngradeConfirmationModalStore } from '@/features/pricing/downgradeConfirmation';
import { useUpgradeConfirmationModalStore } from '@/features/pricing/upgradeConfirmation';

import { rememberPendingSubscriptionCheckout, useBalanceStore } from '@/entities/balance';
import {
  getPricingFeatureLabel,
  trackBusinessPricingClick,
  trackSubscriptionCheckoutCreated,
  useProducts,
} from '@/entities/pricing';
import { useCurrentUser, useFeatureFlags } from '@/entities/user/currentUser';

import { API } from '@/shared/api';
import { PATHS } from '@/shared/constants/routes/paths';
import { PAYMENT_SUBSCRIPTION_CANCELED_PARAM, PAYMENT_SUCCESSFUL_PARAM } from '@/shared/constants/routes/searchParams';
import { usePricingEntryPoint } from '@/shared/contexts/PricingAnalyticsContext';
import { useCustomerPortal } from '@/shared/hooks/useCustomerPortal';
import { useInterfaceLanguage } from '@/shared/i18n';
import { rememberCheckoutEntryPoint } from '@/shared/lib/checkoutEntryPoint';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { GTMEvents } from '@/shared/thirdParty/gtm';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { PaymentInterval, Plan, Price } from '@/shared/types/Billing';

type UseBuyPlanButton = {
  paymentPeriod: PaymentInterval;
  source?: 'pricingPage' | 'trialModal';
  setChosenPlanId: (chosenPlanId: null | string) => void;
};

export const useBuyPlanButton = ({ paymentPeriod, source = 'pricingPage', setChosenPlanId }: UseBuyPlanButton) => {
  const purchaseInProgress = useRef(false);
  const navigate = useNavigate();

  const { data: user } = useCurrentUser();
  const { t } = useInterfaceLanguage();
  const { products } = useProducts();

  const { openCustomerPortal } = useCustomerPortal();
  const openUpgradeConfirmationModal = useUpgradeConfirmationModalStore((state) => state.open);
  const openDowngradeConfirmationModal = useDowngradeConfirmationModalStore((state) => state.open);

  const { featureEnabled: isAppCheckoutEnabled } = useFeatureFlags('new_checkout');

  const { hasActiveSubscription, hasUnpaidSubscription } = useBalanceStore();
  const pricingEntryPoint = usePricingEntryPoint();

  const openStripeCheckout = useCallback(
    async (priceId: Price['id'], plan: Plan) => {
      try {
        const currentUrl = window.location.href;
        const successUrl = new URL(currentUrl);
        const cancelUrl = new URL(currentUrl);

        successUrl.searchParams.append(PAYMENT_SUCCESSFUL_PARAM, '');
        cancelUrl.searchParams.append(PAYMENT_SUBSCRIPTION_CANCELED_PARAM, '');

        const { checkout_url } = await API.getBuySubscriptionCheckoutUrl({
          id: priceId,
          success_url: successUrl.toString(),
          cancel_url: cancelUrl.toString(),
        });

        if (checkout_url) {
          trackSubscriptionCheckoutCreated({ plan, priceId, pricingEntryPoint, selectedInterval: paymentPeriod });
          await rememberPendingSubscriptionCheckout({ interval: paymentPeriod, productId: plan.id });

          window.location.href = checkout_url;
        }
      } catch (e) {
        showErrorAlert(e);
      }
    },
    [paymentPeriod, pricingEntryPoint],
  );

  const navigateToAppCheckout = useCallback(
    (priceId: Price['id']) => {
      navigate(`/${PATHS.checkout}`, { state: { path: `/${PATHS.pricing}`, priceId } });
    },
    [navigate],
  );

  const handleBuyPlanButtonClick = useCallback(
    async (priceId: Price['id'], plan: Plan) => {
      if (purchaseInProgress.current) return;
      purchaseInProgress.current = true;
      try {
        setChosenPlanId(priceId);
        // Every plan action overwrites the entry point, so a stale one from an abandoned checkout can't outlive it.
        rememberCheckoutEntryPoint(pricingEntryPoint);
        trackBusinessPricingClick(plan, priceId, products?.pricing_experiment, source);

        const beginCheckoutGtmData = getPlanBeginCheckoutEventData({ paymentPeriod, plan, priceId });

        GTMEvents.beginCheckout(beginCheckoutGtmData);

        // unpaid subscription
        if (user?.subscription?.id && hasUnpaidSubscription) {
          await openCustomerPortal({
            navigateTo: 'updatePlan',
            priceId,
            subscriptionId: user.subscription.id,
          });
          return;
        }

        // upgrade or downgrade
        if (user?.subscription?.id && hasActiveSubscription) {
          const currentPlan = products?.plan.find((p) => p.id === user.subscription?.plan.product_id);

          const targetPrice = plan.prices.find((price) => price.id === priceId);

          if (currentPlan && targetPrice) {
            const currentPlanOrder = CATEGORY_ORDER[currentPlan.category];
            const targetPlanOrder = CATEGORY_ORDER[plan.category];

            const isLowerCategory = targetPlanOrder < currentPlanOrder;
            const isSameCategory = targetPlanOrder === currentPlanOrder;
            const isLowerBundlePosition = plan.metadata.bundle_position < currentPlan.metadata.bundle_position;
            const isSwitchingToMonthly =
              user.subscription?.plan.interval === PaymentInterval.Year &&
              targetPrice.recurring?.interval === PaymentInterval.Month;
            const isSameBundlePosition = plan.metadata.bundle_position === currentPlan.metadata.bundle_position;

            const isDowngrade =
              isLowerCategory ||
              (isSameCategory && isLowerBundlePosition) ||
              (isSameCategory && isSameBundlePosition && isSwitchingToMonthly);

            if (isDowngrade) {
              MixpanelEvents.downgradePlanSelectedClick({
                currentPlan: currentPlan.name,
                newPlan: plan.name,
              });

              // Calculate features to lose (excluding "All X features, plus:" headers)
              const currentFeatures = featuresByCategory[currentPlan.category] || [];
              const targetFeatures = featuresByCategory[plan.category] || [];
              const targetFeaturesSet = new Set<string>(targetFeatures);
              const featureGroupKeys = new Set(['allBusiness', 'allCreator', 'allCreatorPro']);
              const lostFeatureKeys = currentFeatures.filter(
                (key) => !targetFeaturesSet.has(key) && !featureGroupKeys.has(key),
              );
              const featuresToLose = lostFeatureKeys
                .map((key) => getPricingFeatureLabel(t, key, FEATURES_MAP[key]?.description))
                .filter((desc): desc is string => Boolean(desc));

              openDowngradeConfirmationModal({
                currentPlan,
                featuresToLose,
                targetPlan: plan,
                targetPrice,
              });
            } else {
              MixpanelEvents.upgradePlanSelectedClick({
                currentPlan: currentPlan.name,
                newPlan: plan.name,
              });

              const minutesToAdd = plan.minutes_to_add || 0;

              openUpgradeConfirmationModal({
                currentPlan,
                minutesToAdd,
                targetPlan: plan,
                targetPrice,
              });
            }

            return;
          }
        }

        if (isAppCheckoutEnabled) {
          navigateToAppCheckout(priceId);
          return;
        }

        await openStripeCheckout(priceId, plan);
      } catch (error) {
        showErrorAlert(error);
      } finally {
        purchaseInProgress.current = false;
        setChosenPlanId(null);
      }
    },
    [
      paymentPeriod,
      user,
      products,
      hasActiveSubscription,
      hasUnpaidSubscription,
      isAppCheckoutEnabled,
      pricingEntryPoint,
      setChosenPlanId,
      source,
      openUpgradeConfirmationModal,
      openDowngradeConfirmationModal,
      openStripeCheckout,
      navigateToAppCheckout,
      openCustomerPortal,
      t,
    ],
  );

  return { handleBuyPlanButtonClick };
};
